import React, { useEffect, useRef, useState } from "react";
import AbstractButton from "../components/AbstractButton";
import ForegroundColorSet from "../../utilities/enums/ForegroundColorSet";
import BackgroundColorSet from "../../utilities/enums/BackgroundColorSet";
import { ReactComponent as CancelIcon } from "../../assets/icons/backspace-20-regular.svg?react";
import { ReactComponent as OkayIcon } from "../../assets/icons/checkbox-checked-regular.svg?react";
import { StockCheckExtract, StockCheckExtractLine } from "../../interfaces/Sce";
import { apiFetch } from "../../utilities/AuthenticatedFetch";
import searchIcon from "../../assets/icons/search-blue.svg";
import StockcheckDoneStatic from "../../assets/box_close_endframe.png";
import StockcheckProgress from "../../assets/box_loop_lowres.gif";
import { useNavigate } from "react-router-dom";
import { Slide, toast } from "react-toastify";
import ProgressBar from "@ramonak/react-progress-bar";
import { useDispatch } from "react-redux";
import { clearSavedStockCheck } from "../../store/slices/stockcheckslice";

const StockExtractFinishPage = (props: {
  parentExtract: StockCheckExtract | undefined;
  finishLines: StockCheckExtractLine[] | undefined;
  showFinishPage: boolean;
  searchIconClicked(line: StockCheckExtractLine): void;
  onCancel(): void;
}) => {
  const sendAmount = 50;
  const STOCKCHECK_ENDPOINT = "/partsapp/stock/doStockCheck";

  const [isFinishing, setIsFinishing] = useState(false);
  const [isFinished, setIsFinished] = useState(false);
  const isErrored = useRef(false);
  const [intervalId, setIntervalId] = useState<NodeJS.Timer>();
  const [sendSummaryEmail, setSendSummaryEmail] = useState(true);
  const [batchesComplete, setBatchesComplete] = useState(0);
  const seeVarianceRef = useRef(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const batchesNeeded = useRef(10);

  useEffect(() => {
    if (!isFinishing) {
      if (intervalId) {
        clearInterval(intervalId);
        setIntervalId(undefined);
      }
    }
  }, [isFinishing]);

  const [finishExtract, setFinishExtract] = useState<StockCheckExtract>();

  useEffect(() => {
    // load the parent extract information into finish extract
    if (props.parentExtract && props.finishLines) {
      setFinishExtract({ ...props.parentExtract, lines: props.finishLines });

      if (props.finishLines) {
        if (props.finishLines[0]) {
          if (props.finishLines[0].stockQuantity != null) {
            seeVarianceRef.current = true;
          } else {
            seeVarianceRef.current = false;
          }
        }
      }
    }
  }, [props.showFinishPage]);

  async function saveStockCheck() {
    // send api call to endpoint

    if (finishExtract && finishExtract.lines) {
      batchesNeeded.current = Math.ceil(
        finishExtract.lines.length / sendAmount
      );
      setIsFinishing(true);
      let thisBatchRunId: number | null = null;

      for (let i = 0; i < finishExtract.lines.length; i += sendAmount) {
        const extractToSend = {
          ...finishExtract,
          stockCheckExtractRef: (
            "00000" + finishExtract.stockCheckExtractRef
          ).slice(-5), // LeftPad the StockCheckRef to 5 chars with leading 0s so that SerialisedIsam fetches are not inhibited.
          sendUserSummaryEmail: sendSummaryEmail,
          linesCounted: finishExtract.lines?.length,
          lines: finishExtract.lines
            ?.map((thisLine) => {
              thisLine.quantityCounted = thisLine.qtySubmitted ?? 0;
              return thisLine;
            })
            .slice(i, i + sendAmount),
          eof: i + sendAmount >= finishExtract.lines.length, // is last loop?
          batchRunId: thisBatchRunId,
        };

        const requestOptions: RequestInit = {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(extractToSend),
        };

        try {
          const response = await apiFetch<StockCheckExtract>(
            STOCKCHECK_ENDPOINT,
            requestOptions
          );

          if (response) {
            thisBatchRunId = response.batchRunId;
          }
          setBatchesComplete((prev) => {
            return (prev += 1);
          });
          await sleep(250);
        } catch (err) {
          isErrored.current = true;
          setIsFinishing(false);
          setIsFinished(true);
          toast.error(
            "Error sending Stock Check Extract for Completion: " + err,
            {
              position: "bottom-center",
              autoClose: 3000,
              theme: "colored",
              transition: Slide,
            }
          );
          await sleep(250);
        }
      }
      setIsFinishing(false);
      setIsFinished(true);

      if (props.parentExtract && !isErrored.current) {
        dispatch(
          clearSavedStockCheck(props.parentExtract.stockCheckExtractRef)
        );
      }

      if (!isErrored.current) {
        toast.success("Stock Check completed successfully.", {
          position: "bottom-center",
          autoClose: 3000,
          theme: "colored",
          transition: Slide,
        });
      }
    } else {
      // handle undefined extract
      console.log("Invalid Extract On Save");
    }
  }

  function sleep(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  const doNavAway = (target: string) => {
    // unlock stock check
    apiFetch<StockCheckExtract>("/partsapp/stock/RemoveStockCheckUser", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(props.parentExtract),
    }).finally(() => {
      navigate(target);
    });
  };

  return (
    <>
      <div
        className={
          "finish-page " +
          (props.showFinishPage ? "modal-open" : "modal-closed")
        }
      >
        <div
          id="main-menu-modal-mask"
          onClick={props.onCancel}
          className="modal-mask"
        ></div>
        <div className="modal-page finish-page">
          <div className="info-page-header">
            <p id="finish-page-title">Finish Stock Check Count</p>
          </div>
          {isFinishing && !isErrored.current ? (
            <>
              <div className="info-page-header">
                <p id="finish-page-title">Processing Stock Check</p>
              </div>
              <img className="receive-loading-gif" src={StockcheckProgress} />
              <ProgressBar
                completed={batchesComplete}
                maxCompleted={batchesNeeded.current}
                className="stockcheck-progressbar wrapper"
                baseBgColor="#575757"
                bgColor="#397cfc"
                isLabelVisible={false}
                transitionDuration="0.2s"
                labelColor="#FFF"
              />
              <span className="finish-page-content">In Progress...</span>
              <span className="modal-button-span">
                <AbstractButton
                  id="depot-okay-button"
                  text="Back"
                  specialClass="modal-button"
                  icon={<OkayIcon width="100%" height="100%" />}
                  disabled={true}
                  activeColor={ForegroundColorSet.Green}
                  background={BackgroundColorSet.Black1}
                  actionCallback={function (): unknown {
                    throw new Error("Function not implemented.");
                  }}
                />
              </span>
            </>
          ) : null}

          {!isFinished && !isErrored.current && !isFinishing ? (
            <>
              <hr className="modal-header-underline"></hr>
              <div className="info-page-section">
                Checked quantities will be ready to confirm in Gold.
              </div>

              <div className="hr-container">
                <span className="hr-label">Summary</span>
                <hr className="hr-line" />
              </div>
              <div className="stock-finish-page-variance">
                <div className="scrollable-line-list">
                  <div className="stock-finish-grid-container">
                    <div className="stock-finish-grid-item stock-finish-grid-header">
                      Part
                    </div>
                    <div className="stock-finish-grid-item stock-finish-grid-header">
                      Count
                    </div>
                    <div className="stock-finish-grid-item stock-finish-grid-header">
                      Edit
                    </div>
                    {props.finishLines ? (
                      <>
                        {props.finishLines.map((e) => {
                          let variance = "-";
                          let numericalPositiveVariance = false;
                          if (e.qtySubmitted != e.stockQuantity) {
                            variance = (
                              (e.qtySubmitted ?? 0) -
                              (e.stockQuantity ? e.stockQuantity : 0)
                            ).toString();
                            if (Number(variance) > 0) {
                              numericalPositiveVariance = true;
                            }

                            Number(variance) - Math.floor(Number(variance)) !==
                            0
                              ? (variance = Number(
                                  Number(variance).toFixed(3)
                                ).toString())
                              : "";
                          }
                          return (
                            <>
                              <div
                                id={"finish-line-part-" + e.partNo}
                                className="stock-finish-grid-item"
                              >
                                {e.partNo}
                              </div>

                              <div className="stock-finish-grid-item">
                                <span id={"finish-line-count-" + e.partNo}>
                                  {e.qtySubmitted + " "}
                                </span>
                                {variance != "-" && seeVarianceRef.current ? (
                                  <>
                                    <span className="black4-foreground">(</span>
                                    <span
                                      id={"finish-line-variance-" + e.partNo}
                                      className={
                                        numericalPositiveVariance
                                          ? "green-foreground"
                                          : "red-foreground"
                                      }
                                    >
                                      {(numericalPositiveVariance ? "+" : "") +
                                        variance}
                                    </span>
                                    <span className="black4-foreground">)</span>
                                  </>
                                ) : (
                                  ""
                                )}
                              </div>

                              <div
                                id={"finish-line-searcher-" + e.partNo}
                                className="stock-finish-grid-item"
                              >
                                <img
                                  src={searchIcon}
                                  onClick={() => props.searchIconClicked(e)}
                                  className="finish-search-icon"
                                />
                              </div>
                            </>
                          );
                        })}
                      </>
                    ) : (
                      ""
                    )}
                  </div>
                </div>
              </div>
              <span className="checkbox-label stock-summary-email">
                Send Summary Email
                <input
                  type="checkbox"
                  className="checkbox-style"
                  readOnly
                  checked={sendSummaryEmail}
                  onChange={() => setSendSummaryEmail(!sendSummaryEmail)}
                />
              </span>

              <span className="modal-button-span stock-finish">
                <AbstractButton
                  id="stock-finish-cancel-button"
                  text="Cancel"
                  icon={<CancelIcon width="100%" height="100%" />}
                  disabled={false}
                  activeColor={ForegroundColorSet.Red}
                  background={BackgroundColorSet.Black1}
                  specialClass="modal-button"
                  actionCallback={props.onCancel}
                />
                <AbstractButton
                  id="stock-finish-save-button"
                  text="Input"
                  icon={<OkayIcon width="100%" height="100%" />}
                  disabled={false}
                  activeColor={ForegroundColorSet.Green}
                  background={BackgroundColorSet.Black1}
                  specialClass="modal-button"
                  actionCallback={saveStockCheck}
                />
              </span>
            </>
          ) : null}
          {isFinished ? (
            <>
              <div className="info-page-header">
                <p
                  id="finish-page-title"
                  className="stock-extract-finish-text-wrap"
                >
                  Stock Check Processed
                </p>
              </div>
              <img className="receive-loading-gif" src={StockcheckDoneStatic} />
              <span className="modal-button-span">
                <AbstractButton
                  id="depot-okay-button"
                  text="Back"
                  specialClass="modal-button"
                  icon={<OkayIcon width="100%" height="100%" />}
                  disabled={false}
                  activeColor={ForegroundColorSet.Green}
                  background={BackgroundColorSet.Black1}
                  actionCallback={() => {
                    doNavAway("/stockcheck");
                  }}
                />
              </span>
            </>
          ) : null}
        </div>
      </div>
    </>
  );
};

export default StockExtractFinishPage;
